import GatewayBase from './gateway-base';
import { OrderTrackingStatus, PaginatedResponse } from '@monolog/shared/types';
import { Orders } from '@database/types';
import { of } from 'rxjs';

type OrderItem = {
  id: string;
  currency: string;
  orderId: string;
  productName: string;
  productUrl: string;
  productImage: string;
  itemDescription: string;
  sku: string;
  price: number;
  discount: number;
  tax: number;
  sourceItemId: string;
  sourceProductId: string;
  sourceVariantId: string;
  refundedQuantity: number;
  manuallyAdded: boolean;
  selectedOptions: { name: string; value: string }[];
};

type ReturnItem = {
  id: string;
  returnId: string;
  productName: string;
  orderItem: OrderItem;
  quantity: number;
  reason: string;
  reasonCode: string;
};

export type Return = {
  provider: Orders.ReturnProvider;
  id: string;
  orderId: string;
  status: string;
  url: string;
  trackingId: string;
  trackingNumber: string;
  trackingCarrier: string;
  trackingUrl: string;
  sourceCreatedAt: string;
  createdAt: string;
  updatedAt: string;
  returnNumber: string;
  items: ReturnItem[];
  labelUrl: string;
  labelCost: string;
  labelCurrency: string;
};

export class OrdersRestService extends GatewayBase {
  get products() {
    return {
      cancelSyncShopify: this.cancelSyncShopifyProducts,
      delete: this.deleteProduct,
      download: {
        products: this.downloadProductsTemplate,
        warranties: this.downloadWarrantyTemplate,
      },
      get: this.getProducts,
      getChildren: this.getProductChildren,
      getVariants: this.getProductVariants,
      search: this.searchProducts,
      syncLog: {
        get: this.getProductSyncLog,
      },
      syncShopify: this.syncShopifyProducts,
      taxonomy: {
        downloadTemplate: this.downloadTaxonomyTemplate,
        uploadFile: this.uploadTaxonomyFile,
      },
      upload: this.uploadProducts,
      uploadFile: this.uploadProductsFile,
      warranties: {
        cancelUpload: this.cancelWarrantyUpload,
        update: this.updateProductWarranties,
        upload: this.uploadWarrantyProducts,
        uploadFile: this.uploadWarrantyProductsFile,
      },
      warrantyStatuses: {
        downloadTemplate: this.downloadWarrantyStatusTemplate,
        update: this.updateWarrantyStatuses,
        uploadFile: this.uploadWarrantyStatusFile,
      },
    };
  }

  get orders() {
    return {
      address: {
        update: this.updateAddress,
      },
      checkChargeback: this.checkChargeback,
      counts: {
        forStores: this.getOrdersCountsForStores,
      },
      customerEmail: {
        update: this.updateCustomerEmail,
      },
      export: this.exportOrders,
      findById: this.getOrderById,
      forStores: this.getOrdersForStores,
      getOrderRefunds: this.getOrderRefunds,
      getRootOrder: this.getRootOrder,
      search: this.searchOrders,
      trackingStatus: this.getTrackingStatus,
    };
  }

  get returns() {
    return {
      byOrder: this.getReturnsByOrderId,
      customers: {
        byId: this.getCustomerById,
        byStoreId: this.getAllCustomersByStoreId,
        protectedOrders: this.getCustomerProtectedOrders,
      },
      list: this.listReturns,
      settings: {
        get: this.getReturnSettings,
        update: this.updateReturnSettings,
      },
    };
  }

  private listReturns = (storeIds: string[], filters: ReturnType<typeof useFilters>, pagination: any) => {
    if (storeIds.length === 0) {
      return of({ data: { returns: [] } as { returns: Return[] } });
    }

    const filtersQuery = filters.getQuery([]);
    const params: Record<string, any> = {
      filters: filtersQuery ? JSON.stringify(filtersQuery) : '',
      limit: pagination.info.rowsPerPage,
      page: pagination.info.page - 1,
    };

    return this.client.get<{ returns: Return[] }>(`/v1/returns`, { params });
  };

  private searchOrders = (params?: { filters?: string }) => {
    return this.client.get<any>(`/v1/orders/search/${params?.filters}`);
  };

  private exportOrders = (body: any) => {
    return this.client.post<any>(`/v1/orders/export-data`, body);
  };

  private getOrdersCountsForStores = (params = {}) => {
    return this.client.get<{ totalCount: number }>(`/v1/orders/store/counts`, {
      params,
    });
  };

  private getOrdersForStores = (params = {}) => {
    return this.client.get<{ orders: any[]; count: number; pageInfo: any }>(`/v1/orders/list/store`, {
      params,
    });
  };

  private getOrderById = (sourceOrderId: string) => {
    return this.client.get<Orders.Order>(`/v1/orders/${sourceOrderId}/full`);
  };

  private checkChargeback = (orderId: string) => {
    return this.client.get<{ hasChargeback: boolean }>(`/v1/orders/${orderId}/chargeback`);
  };

  private getOrderRefunds = (orderId: string) => {
    return this.client.get<{ totals: { item: number; shipping: number; order: number } }>(
      `/v1/orders/${orderId}/refunds`,
    );
  };

  private getRootOrder = (orderId: string, storeId: string) => {
    return this.client.get<Orders.Order>(`/v1/orders/${orderId}/root`, { params: { storeId } });
  };

  private getTrackingStatus = (orderId: string) => {
    return this.client.get<OrderTrackingStatus>(`/v1/orders/${orderId}/tracking-status`);
  };

  private getReturnsByOrderId = (orderId: string) => {
    return this.client.get<Return[]>(`v1/returns/${orderId}`);
  };

  private getReturnSettings = (storeId: string, provider = 'parcellab') => {
    return this.client.get<Orders.ReturnSetting>(`v1/returns/${provider}/settings/store/${storeId}`);
  };

  private updateReturnSettings = (storeId: string, input: Partial<Orders.ReturnSetting>, provider = 'parcellab') => {
    return this.client.put<Orders.ReturnSetting>(`v1/returns/${provider}/settings/store/${storeId}`, input);
  };

  private getAllCustomersByStoreId = (storeId: string, paginationArgs = {}) => {
    return this.client.get<
      PaginatedResponse<{
        id: string;
        name: string;
        email: string;
        phone: string;
        createdAt: string;
        updatedAt: string;
        dataPoints: {
          insuredOrders: number;
          totalAmount: number;
          totalOrders: number;
        };
      }>
    >(`v1/orders/customers/all`, {
      params: { storeId, store_id: storeId, ...paginationArgs },
    });
  };

  private getCustomerById = (customerId: string, storeId: string) => {
    return this.client.get<Orders.Customer>(`v1/orders/customers/${customerId}`, {
      params: { storeId, store_id: storeId },
    });
  };

  private getCustomerProtectedOrders = (customerId: string, storeId: string, paginationArgs = {}) => {
    return this.client.get<PaginatedResponse<Orders.Order>>(`v1/orders/customers/${customerId}/protected`, {
      params: { storeId, store_id: storeId, ...paginationArgs },
    });
  };

  private uploadProducts = (storeId: string, body: any) => {
    return this.client.post<any>(`v1/orders/${storeId}/products/upload-csv`, body);
  };

  private uploadProductsFile = (storeId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.post<any>(`v1/orders/${storeId}/products/upload-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  private syncShopifyProducts = (storeId: string) => {
    return this.client.post<any>(`v1/orders/${storeId}/products/syncShopify`);
  };

  private cancelSyncShopifyProducts = (storeId: string) => {
    return this.client.post<any>(`v1/orders/${storeId}/products/cancel-sync`);
  };

  private cancelWarrantyUpload = (storeId: string) => {
    return this.client.post<any>(`v1/orders/${storeId}/products/cancel-warranty-upload`);
  };

  private getProductSyncLog = (
    storeId: string,
    options?: {
      status?: Orders.ProductSyncStatus;
      source?: Orders.ProductSyncSource;
      type?: Orders.ProductSyncType;
      limit?: number;
    },
  ) => {
    return this.client.get<any>(`v1/orders/${storeId}/products/sync-log`, { params: options });
  };

  private downloadProductsTemplate = (storeId: string) => {
    return this.client.post(`v1/orders/${storeId}/products/download-products-template`);
  };

  private downloadWarrantyTemplate = (storeId: string) => {
    return this.client.post(`v1/orders/${storeId}/products/download-warranty-template`);
  };

  private downloadTaxonomyTemplate = (storeId: string) => {
    return this.client.post(`v1/orders/${storeId}/products/download-taxonomy-template`);
  };

  private uploadTaxonomyFile = (storeId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.post<{
      created: number;
      parents: number;
      stubsCreated: number;
      errors: string[];
      totalRows: number;
      skippedRows: number;
    }>(`v1/orders/${storeId}/products/upload-taxonomy-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  private uploadWarrantyProducts = (storeId: string, body: any) => {
    return this.client.post<any>(`v1/orders/${storeId}/products/upload-warranty-csv`, body);
  };

  private uploadWarrantyProductsFile = (storeId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.post<any>(`v1/orders/${storeId}/products/upload-warranty-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  private getProducts = (storeId: string, params: any) => {
    return this.client.get<any>(`v1/orders/${storeId}/products`, { params });
  };

  private getProductVariants = (storeId: string, productId: string) => {
    return this.client.get<any>(`v1/orders/${storeId}/products/${productId}/variants`);
  };

  private getProductChildren = (storeId: string, productId: string, variantId?: string | null) => {
    return this.client.get<any>(`v1/orders/${storeId}/products/${productId}/children`, {
      params: variantId ? { variantId } : {},
    });
  };

  private searchProducts = ({
    productName,
    storeId,
    limit = 15,
    skip = 0,
  }: {
    productName: string;
    storeId: string;
    limit?: number;
    skip?: number;
  }) => {
    return this.client.get(
      `v1/orders/${storeId}/products/query?name=${encodeURIComponent(productName)}&limit=${limit}&skip=${skip}`,
    );
  };

  private updateAddress = (sourceOrderId: string, address: any) => {
    return this.client.put<any>(`v1/orders/${sourceOrderId}/address`, address);
  };

  private updateCustomerEmail = (orderId: string, customerEmail: string) => {
    return this.client.put<any>(`v1/orders/${orderId}/customer-email`, { customerEmail });
  };

  private updateProductWarranties = (storeId: string, body: any) => {
    return this.client.patch<any>(`v1/orders/${storeId}/products/warranties/update`, body);
  };

  private deleteProduct = (storeId: string, productId: string) => {
    return this.client.delete<any>(`v1/orders/${storeId}/products/${productId}`);
  };

  private updateWarrantyStatuses = (
    storeId: string,
    body: {
      statuses: Array<{ productId: string; warrantyStatus: string }>;
    },
  ) => {
    return this.client.patch<{ updated: number; productIds: string[] }>(
      `v1/orders/${storeId}/products/warranty/statuses`,
      body,
    );
  };

  private downloadWarrantyStatusTemplate = (storeId: string) => {
    return this.client.get<string>(`v1/orders/${storeId}/products/warranty/download-status-template`);
  };

  private uploadWarrantyStatusFile = (storeId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.post<{ updated: number; productIds: string[]; errors: string[]; totalRows: number }>(
      `v1/orders/${storeId}/products/warranty/upload-status-file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  };
}
